package com.dotdrives.web.pages.giving

import com.dotdrives.controller.PublicGivingController
import com.dotdrives.controller.PublicGivingUiState
import com.dotdrives.model.*
import kotlin.math.roundToInt
import kotlin.math.pow
import kotlinx.serialization.json.*
import react.FC
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.footer
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.strong
import web.cssom.ClassName
import web.html.button
import web.html.submit

external interface PublicGivingPageProps : Props {
    var state: PublicGivingUiState
    var controller: PublicGivingController
    var paymentReturn: String?
    var missingReturn: Boolean
    var onNewGift: () -> Unit
}

val PublicGivingUnavailable =
    FC<Props> {
        PublicGivingStyles()
        main {
            className = ClassName("give-unavailable")
            h1 { +"This giving form is unavailable" }
            p {
                +"The link may be invalid, or the campaign may be closed. Please contact the organization for a current giving link."
            }
            a {
                href = "#/"
                +"Dot Drives home"
            }
        }
    }

val PublicGivingPage =
    FC<PublicGivingPageProps> { props ->
        val state = props.state
        val controller = props.controller
        val form = state.form
        PublicGivingStyles()
        if (state.restorationFailed) {
            main {
                className = ClassName("give-unavailable")
                h1 { +"Restore your saved payment" }
                p { +state.error.orEmpty() }
                button {
                    onClick = { controller.restore() }
                    +"Retry saved payment"
                }
            }
        } else if (state.loading) {
            main {
                className = ClassName("give-unavailable")
                ariaBusy = true
                h1 { +"Loading giving form…" }
                div { className = ClassName("give-skeleton") }
            }
        } else if (form == null) {
            main {
                className = ClassName("give-unavailable")
                h1 {
                    +if (state.completed && !props.missingReturn) "Thank you for your gift"
                    else if (state.attempt?.statusToken != null) "Check your payment"
                    else "This giving form is unavailable"
                }
                if (state.completed && !props.missingReturn)
                    p { +"Your payment has been confirmed by the giving service." }
                else if (state.attempt?.statusToken != null) {
                    p {
                        +"This campaign is no longer available, but you can still check your original payment. A return link alone does not confirm payment."
                    }
                    button {
                        disabled = state.busy
                        onClick = { controller.checkStatus() }
                        +"Check payment status"
                    }
                }
                p {
                    +(state.error
                        ?: "The campaign may be closed. Please check the link with the organization.")
                }
                button {
                    onClick = { controller.load(state.attempt) }
                    +"Try again"
                }
            }
        } else {
            main {
                className = ClassName("give-layout")
                form.config.design.backgroundColor?.takeIf { Regex("#[0-9a-fA-F]{6}").matches(it) }?.let { accent ->
                    asDynamic().style = js("({})")
                    asDynamic().style["--give-accent"] = accent
                    asDynamic().style["--give-accent-text"] = givingAccentText(accent)
                }
                GivingStory {
                    this.state = state
                    this.controller = controller
                }
                section {
                    className = ClassName("give-panel")
                    div {
                        className = ClassName("give-form")
                        if (state.completed && !props.missingReturn) {
                            div {
                                className = ClassName("give-status")
                                h2 { +"Thank you for your gift" }
                                p { +"Your payment has been confirmed by the giving service." }
                                p { +"Thank you for supporting ${form.orgName}." }
                                button {
                                    className = ClassName("give-primary")
                                    onClick = { props.onNewGift() }
                                    +"Make another gift"
                                }
                            }
                        } else {
                            h2 { +"Make a difference today" }
                            p {
                                className = ClassName("give-intro")
                                +"Choose your gift to ${form.orgName}."
                            }
                            if (state.isPreview)
                                div {
                                    className = ClassName("give-notice")
                                    strong { +"Preview · Payments disabled" }
                                    p { +"Try amounts, giving frequency and contact fields. Nothing you enter is submitted or saved." }
                                }
                            else if (!form.paymentsReady)
                                div {
                                    className = ClassName("give-notice")
                                    strong { +"Online giving is not available yet" }
                                    p {
                                        +"You can explore this form. Please contact ${form.orgName} about other ways to give, or return later."
                                    }
                                }
                            if (props.missingReturn)
                                div {
                                    className = ClassName("give-notice")
                                    +"We can’t verify this return in this browser. A return link does not confirm payment. Please contact the organization before giving again."
                                }
                            if (props.paymentReturn == "canceled")
                                div {
                                    className = ClassName("give-notice")
                                    +"You returned from checkout. Your payment has not been confirmed. You can reopen the same checkout or check its status."
                                }
                            if (state.attempt != null)
                                div {
                                    className = ClassName("give-notice")
                                    strong {
                                        +when {
                                            state.reconciliationRequired -> "Payment needs review"
                                            state.paymentStatus == "failed" ->
                                                "Payment was not completed"
                                            else -> "Payment not yet confirmed"
                                        }
                                    }
                                    p {
                                        +if (state.reconciliationRequired)
                                            "Please contact the organization before trying again."
                                        else
                                            "Your original gift details are saved for a safe retry. Please avoid starting another payment while this one is pending."
                                    }
                                    if (
                                        state.attempt?.statusToken != null &&
                                            !state.reconciliationRequired
                                    )
                                        button {
                                            className = ClassName("give-secondary")
                                            disabled = state.busy
                                            onClick = { controller.checkStatus() }
                                            +"Check payment status"
                                        }
                                    if (
                                        state.attempt?.statusToken == null &&
                                            !state.reconciliationRequired
                                    ) {
                                        p {
                                            +"To change these details, we must first verify that this checkout can be safely retired."
                                        }
                                        button {
                                            className = ClassName("give-secondary")
                                            disabled = state.busy
                                            onClick = { controller.reviseGift() }
                                            +"Revise gift safely"
                                        }
                                    }
                                    if (
                                        state.paymentStatus == "failed" &&
                                            !state.reconciliationRequired
                                    )
                                        button {
                                            className = ClassName("give-secondary")
                                            onClick = { props.onNewGift() }
                                            +"Start a new gift"
                                        }
                                }
                            form {
                                onSubmit = {
                                    it.preventDefault()
                                    if (!state.isPreview && !props.missingReturn) controller.submit()
                                }
                                GivingGiftFields {
                                    this.state = state
                                    this.controller = controller
                                }
                                GivingContactFields {
                                    this.state = state
                                    this.controller = controller
                                }
                                GivingEmployerMatch {
                                    this.state = state
                                    this.controller = controller
                                }
                                state.error?.let {
                                    div {
                                        className = ClassName("give-error")
                                        role = "alert".unsafeCast<AriaRole>()
                                        +it
                                    }
                                }
                                div {
                                    className = ClassName("give-checkout")
                                    div {
                                        className = ClassName("give-total")
                                        span {
                                            +when (state.frequency) {
                                                "monthly" -> "Monthly total"
                                                "quarterly" -> "Quarterly total"
                                                else -> "Gift total"
                                            }
                                        }
                                        strong { +money(state.totalCents / 100.0) }
                                    }
                                    p {
                                        +if (state.isPreview) "Payments are disabled in preview." else "You’ll enter payment details on Stripe’s secure checkout."
                                    }
                                    button {
                                        className = ClassName("give-primary")
                                        type = web.html.ButtonType.submit
                                        disabled =
                                            state.isPreview || state.busy ||
                                                (!form.paymentsReady && state.attempt == null) ||
                                                props.missingReturn ||
                                                state.reconciliationRequired ||
                                                state.paymentStatus == "failed"
                                        +when {
                                            state.isPreview -> "Payments disabled in preview"
                                            state.busy -> "Please wait…"
                                            state.attempt != null -> "Reopen original checkout"
                                            !form.paymentsReady -> "Online giving unavailable"
                                            else -> "Continue to secure checkout →"
                                        }
                                    }
                                }
                            }
                        }
                    }
                    footer { +if (state.isPreview) "Preview · Powered by Dot Drives" else "Secure checkout · Powered by Dot Drives" }
                }
            }
        }
    }

internal fun money(amount: Double): String {
    val number =
        kotlin.math
            .abs(amount)
            .asDynamic()
            .toLocaleString("en-US", js("({minimumFractionDigits:2,maximumFractionDigits:2})"))
            as String
    return "${if (amount < 0) "−" else ""}$$number"
}

internal fun redundantAmountLabel(amount: FormAmount): Boolean =
    givingCents(amount.label.trim().removePrefix("$").replace(",", "")) ==
        (amount.amount * 100).roundToInt()

/** Choose the higher-contrast foreground for any organization accent. */
internal fun givingAccentText(accent: String): String {
    fun linear(channel: Int): Double {
        val value = channel / 255.0
        return if (value <= .04045) value / 12.92 else ((value + .055) / 1.055).pow(2.4)
    }
    val luminance = .2126 * linear(accent.substring(1,3).toInt(16)) +
        .7152 * linear(accent.substring(3,5).toInt(16)) + .0722 * linear(accent.substring(5,7).toInt(16))
    return if (luminance > .179) "#000000" else "#FFFFFF"
}
